import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  AssignCoursesRequest,
  AdminCourseCatalogResponse,
  AdminLessonBunnyVideoCatalogResponse,
  AssignBunnyVideoRequest,
  CourseVideoExcelImportResult,
  AuditLogSummary,
  CertificateDetailResponse,
  PublicCertificateVerification,
  CertificateSummary,
  CertificateTemplateResponse,
  CompanyDetailResponse,
  CompanySummary,
  CourseSummary,
  CreateCompanyRequest,
  CreateDriverRequest,
  CreateOrganizationRequest,
  DashboardSummary,
  DriverDetailResponse,
  DriverEvaluationReportResponse,
  DriverListResponse,
  DriverMailActionResponse,
  DriverGroupOption,
  DriverSummary,
  DriverTrashItem,
  DriverPaymentResponse,
  DriverPaymentType,
  StripePortalResponse,
  EnrollmentLearningResponse,
  LearningHomeResponse,
  LessonLearningResponse,
  LessonProgressUpdateRequest,
  LessonQuizResultResponse,
  LessonQuizSubmitRequest,
  FinalQuizResponse,
  FinalQuizResultResponse,
  LoginRequest,
  LoginResponse,
  MeResponse,
  OrganizationSummary,
  PageResponse,
  PasswordResetCompleteRequest,
  PasswordResetCompleteResponse,
  PasswordResetRequest,
  PasswordResetRequestResponse,
  UpdateCertificateTemplateRequest,
  StripeCheckoutResponse
} from '../models/portal.models';

@Injectable({ providedIn: 'root' })
export class ApiClientService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = (window as Window & { __DRIVEME_CONFIG__?: { apiBaseUrl?: string } }).__DRIVEME_CONFIG__?.apiBaseUrl ?? '/api';

  dashboard() {
    return this.http.get<DashboardSummary>(`${this.baseUrl}/dashboard`);
  }

  me() {
    return this.http.get<MeResponse>(`${this.baseUrl}/me`);
  }

  login(request: LoginRequest) {
    return this.http.post<LoginResponse>(`${this.baseUrl}/auth/login`, request);
  }

  requestPasswordReset(request: PasswordResetRequest) {
    return this.http.post<PasswordResetRequestResponse>(`${this.baseUrl}/auth/password-reset/request`, request);
  }

  completePasswordReset(request: PasswordResetCompleteRequest) {
    return this.http.post<PasswordResetCompleteResponse>(`${this.baseUrl}/auth/password-reset/complete`, request);
  }

  organizations(page = 0, size = 25, search = '', status = '') {
    const params: Record<string, string | number> = { page, size };

    if (search.trim()) {
      params['search'] = search.trim();
    }

    if (status.trim()) {
      params['status'] = status.trim();
    }

    return this.http.get<PageResponse<OrganizationSummary>>(`${this.baseUrl}/organizations`, { params });
  }

  createOrganization(request: CreateOrganizationRequest) {
    return this.http.post<OrganizationSummary>(`${this.baseUrl}/organizations`, request);
  }

  updateOrganization(id: number, request: CreateOrganizationRequest) {
    return this.http.put<OrganizationSummary>(`${this.baseUrl}/organizations/${id}`, request);
  }

  deleteOrganization(id: number) {
    return this.http.delete<void>(`${this.baseUrl}/organizations/${id}`);
  }

  auditLogs(page = 0, size = 50, search = '', action = '') {
    const params: Record<string, string | number> = { page, size };

    if (search.trim()) {
      params['search'] = search.trim();
    }

    if (action.trim()) {
      params['action'] = action.trim();
    }

    return this.http.get<PageResponse<AuditLogSummary>>(`${this.baseUrl}/audit-logs`, { params });
  }

  drivers(page = 0, size = 25, search = '', companyId = '', status = '') {
    const params: Record<string, string | number> = { page, size };

    if (search.trim()) {
      params['search'] = search.trim();
    }

    if (companyId.trim()) {
      params['companyId'] = companyId.trim();
    }

    if (status.trim()) {
      params['status'] = status.trim();
    }

    return this.http.get<DriverListResponse>(`${this.baseUrl}/drivers`, { params });
  }

  companies(page = 0, size = 25, search = '', status = '') {
    const params: Record<string, string | number> = { page, size };

    if (search.trim()) {
      params['search'] = search.trim();
    }

    if (status.trim()) {
      params['status'] = status.trim();
    }

    return this.http.get<PageResponse<CompanySummary>>(`${this.baseUrl}/companies`, { params });
  }

  createCompany(request: CreateCompanyRequest) {
    return this.http.post<CompanySummary>(`${this.baseUrl}/companies`, request);
  }

  company(id: number) {
    return this.http.get<CompanyDetailResponse>(`${this.baseUrl}/companies/${id}`);
  }

  updateCompany(id: number, request: CreateCompanyRequest) {
    return this.http.put<CompanySummary>(`${this.baseUrl}/companies/${id}`, request);
  }

  deleteCompany(id: number) {
    return this.http.delete<void>(`${this.baseUrl}/companies/${id}`);
  }

  createDriver(request: CreateDriverRequest) {
    return this.http.post<DriverSummary>(`${this.baseUrl}/drivers`, request);
  }

  driver(id: number) {
    return this.http.get<DriverDetailResponse>(`${this.baseUrl}/drivers/${id}`);
  }

  driverEvaluationReport(driverId: number, enrollmentId: number) {
    return this.http.get<DriverEvaluationReportResponse>(
      `${this.baseUrl}/drivers/${driverId}/enrollments/${enrollmentId}/failed-attempts`
    );
  }

  driverGroups() {
    return this.http.get<DriverGroupOption[]>(`${this.baseUrl}/drivers/groups`);
  }

  updateDriver(id: number, request: CreateDriverRequest) {
    return this.http.put<DriverSummary>(`${this.baseUrl}/drivers/${id}`, request);
  }

  deleteDriver(id: number) {
    return this.http.delete<void>(`${this.baseUrl}/drivers/${id}`);
  }

  deletedDrivers(page = 0, size = 50) {
    return this.http.get<PageResponse<DriverTrashItem>>(`${this.baseUrl}/drivers/trash`, { params: { page, size } });
  }

  restoreDriver(id: number) {
    return this.http.post<DriverSummary>(`${this.baseUrl}/drivers/${id}/restore`, {});
  }

  sendDriverInvitation(id: number) {
    return this.http.post<DriverMailActionResponse>(`${this.baseUrl}/drivers/${id}/invitation`, {});
  }

  sendDriverReminder(id: number) {
    return this.http.post<DriverMailActionResponse>(`${this.baseUrl}/drivers/${id}/reminder`, {});
  }

  assignDriverCourses(id: number, request: AssignCoursesRequest) {
    return this.http.post<DriverDetailResponse>(`${this.baseUrl}/drivers/${id}/assignments`, request);
  }

  courses() {
    return this.http.get<CourseSummary[]>(`${this.baseUrl}/courses`);
  }

  learningHome() {
    return this.http.get<LearningHomeResponse>(`${this.baseUrl}/learning`);
  }

  driverPaymentStatus() {
    return this.http.get<DriverPaymentResponse>(`${this.baseUrl}/payments/status`);
  }

  createDriverCheckoutSession(paymentType: DriverPaymentType) {
    return this.http.post<StripeCheckoutResponse>(`${this.baseUrl}/payments/checkout`, { paymentType });
  }

  recordDriverPaymentReturn() {
    return this.http.post<void>(`${this.baseUrl}/payments/return`, {});
  }

  createDriverPortalSession() {
    return this.http.post<StripePortalResponse>(`${this.baseUrl}/payments/portal`, {});
  }

  learningEnrollment(enrollmentId: number) {
    return this.http.get<EnrollmentLearningResponse>(`${this.baseUrl}/learning/enrollments/${enrollmentId}`);
  }

  learningLesson(enrollmentId: number, lessonId: number) {
    return this.http.get<LessonLearningResponse>(`${this.baseUrl}/learning/enrollments/${enrollmentId}/lessons/${lessonId}`);
  }

  updateLessonProgress(enrollmentId: number, lessonId: number, request: LessonProgressUpdateRequest) {
    return this.http.post<LessonLearningResponse>(
      `${this.baseUrl}/learning/enrollments/${enrollmentId}/lessons/${lessonId}/progress`,
      request
    );
  }

  submitLessonQuiz(enrollmentId: number, lessonId: number, request: LessonQuizSubmitRequest) {
    return this.http.post<LessonQuizResultResponse>(
      `${this.baseUrl}/learning/enrollments/${enrollmentId}/lessons/${lessonId}/quiz`,
      request
    );
  }

  certificates(page = 0, size = 25) {
    return this.http.get<PageResponse<CertificateSummary>>(`${this.baseUrl}/certificates`, {
      params: { page, size }
    });
  }

  certificate(id: number) {
    return this.http.get<CertificateDetailResponse>(`${this.baseUrl}/certificates/${id}`);
  }

  finalQuiz(enrollmentId: number) {
    return this.http.get<FinalQuizResponse>(`${this.baseUrl}/learning/enrollments/${enrollmentId}/final-quiz`);
  }

  submitFinalQuiz(enrollmentId: number, request: LessonQuizSubmitRequest) {
    return this.http.post<FinalQuizResultResponse>(
      `${this.baseUrl}/learning/enrollments/${enrollmentId}/final-quiz`,
      request
    );
  }

  publicCertificateVerification(token: string) {
    return this.http.get<PublicCertificateVerification>(`${this.baseUrl}/public/certificates/verify/${token}`);
  }

  downloadCertificatePdf(id: number) {
    return this.http.get(`${this.baseUrl}/certificates/${id}/pdf`, {
      responseType: 'blob'
    });
  }

  certificateTemplate() {
    return this.http.get<CertificateTemplateResponse>(`${this.baseUrl}/certificate-template`);
  }

  updateCertificateTemplate(request: UpdateCertificateTemplateRequest) {
    return this.http.put<CertificateTemplateResponse>(`${this.baseUrl}/certificate-template`, request);
  }

  downloadCertificateTemplate() {
    return this.http.get(`${this.baseUrl}/certificate-template/download`, {
      responseType: 'blob'
    });
  }

  adminCourseCatalog() {
    return this.http.get<AdminCourseCatalogResponse>(`${this.baseUrl}/admin/courses`);
  }

  adminLessonBunnyVideos(lessonId: number) {
    return this.http.get<AdminLessonBunnyVideoCatalogResponse>(`${this.baseUrl}/admin/lessons/${lessonId}/bunny-videos`);
  }

  assignBunnyVideoToLesson(lessonId: number, request: AssignBunnyVideoRequest) {
    return this.http.post(`${this.baseUrl}/admin/lessons/${lessonId}/bunny-videos/assign`, request);
  }

  importCourseVideos(file: File) {
    const body = new FormData();
    body.append('file', file, file.name);
    return this.http.post<CourseVideoExcelImportResult>(`${this.baseUrl}/admin/course-video-import`, body);
  }

  importQuestions(file: File, dryRun: boolean) {
    const body = new FormData();
    body.append('file', file, file.name);
    return this.http.post<{ questions: number; lessonQuestions: number; finalQuestions: number;
      questionsCreated: number; questionsUpdated: number; dryRun: boolean }>(
      `${this.baseUrl}/admin/question-import`, body, { params: { dryRun } });
  }

  importCourseContent(file: File) {
    const body = new FormData();
    body.append('file', file, file.name);
    return this.http.post<{ videoSourceRows: number; videosWithUrl: number; coursesCreated: number;
      coursesUpdated: number; lessonsCreated: number; lessonsUpdated: number; questions: number;
      lessonQuestions: number; finalQuestions: number; questionsCreated: number; questionsUpdated: number }>(
      `${this.baseUrl}/admin/course-content-import`, body);
  }
}
